/**
 * Low-level wrappers around `window.dataLayer.push` for Google Tag Manager (GTM).
 *
 * Responsibilities:
 *  - Provide a single, SSR-safe entry point (`pushDataLayer`) that other analytics
 *    helpers reuse so callers never touch `window.dataLayer` directly.
 *  - Expose a typed function per business event (`object_search_start`,
 *    `map_search_view`, `list_search_view`, `object_view`, `filter_interaction`,
 *    `object_inquiry`, `login`, contact interactions, ...).
 *    GTM triggers / tags listen for these `event` names in the Tag Manager workspace.
 *
 * The GTM container snippet itself is injected in `src/app/layout.tsx` (id `GTM-KPQXMF9Z`).
 * `window.dataLayer` is initialised by the GTM loader and re-declared defensively here
 * to keep this module safe to call before the GTM snippet has executed (e.g. during
 * hydration or in unit tests where the snippet is absent).
 *
 * See `doc/GTM.md` for the full event catalogue and payload shapes.
 */

/**
 * Pushes an arbitrary payload onto `window.dataLayer`.
 *
 * No-op on the server: every other helper in this file funnels through here so we
 * only need one guard. The `window.dataLayer` array is lazily created if GTM has
 * not yet run; GTM's snippet tolerates a pre-existing array and will consume it.
 */
export function pushDataLayer(payload: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * `object_search_start` — fired when a housing list search request runs with
 * `trackAsSearch: true` (`useGetHousingList` on `/housing`). Payload is built
 * by `buildObjectSearchStartPayload` (request body + German category titles).
 * `search_source` is `list` for `/search/propileya` and `map` for `/search/map/propileya`.
 */
export function pushObjectSearchStart(payload: Record<string, unknown>) {
  pushDataLayer({ event: 'object_search_start', ...payload });
}

/** `map_search_view` — housing search is showing the map. */
export function pushMapSearchView() {
  pushDataLayer({ event: 'map_search_view' });
}

/** `list_search_view` — housing search is showing the list or the tile grid. */
export function pushListSearchView() {
  pushDataLayer({ event: 'list_search_view' });
}

/**
 * `filter_interaction` — fired for each individual filter value that was
 * added or removed on the housing list. Emitted (debounced) by
 * `useHousingListFilterInteractionAnalytics` via `filterInteractionPayload.ts`.
 *
 * `filter_group` uses German keys from the spec (e.g. `angebotstyp`,
 * `immobilienart`, `ausstattung`, `price_min`, `price_max`).
 */
export function pushFilterInteraction(payload: {
  filter_group: string;
  filter_value: string;
  filter_action: 'add' | 'remove';
  search_source: 'list' | 'map';
}) {
  pushDataLayer({ event: 'filter_interaction', ...payload });
}

/**
 * `object_inquiry` — fired after a successful listing enquiry (contact form
 * or WhatsApp). Payload is assembled by `buildObjectInquiryPayload` from the
 * housing object API response and the `inquiry_type` discriminator.
 */
export function pushObjectInquiry(payload: Record<string, unknown>) {
  pushDataLayer({ event: 'object_inquiry', ...payload });
}

/**
 * `object_view` — the user opened a detail page (`/housing/:id` or `/project/:projectId`).
 * Fired once per object id from `ObjectViewTracker` after mount (SSR-safe).
 * `search_source` — which search the user came from; `direct` when not opened from search.
 * Same key as on `object_search_start`, so GTM keeps a single variable.
 * `isProject` — `true` on a new-build project page (`/project/:projectId`).
 */
export type ObjectViewSource = 'list' | 'map' | 'direct';

export function pushObjectView(
  object_id: string,
  search_source: ObjectViewSource,
  isProject: boolean,
) {
  pushDataLayer({ event: 'object_view', object_id, search_source, isProject });
}

/**
 * `login` — fired exactly once per real authentication transition (i.e. when
 * a fresh, valid access token is persisted without a previous valid one).
 * Token-refresh cycles must not re-emit this; see `contexts/auth/utils.ts`.
 */
export function pushLogin() {
  pushDataLayer({ event: 'login' });
}

/**
 * `register` — fired exactly once after a new user registers via the portal.
 * Triggered when the access token payload contains `new_registration: "true"`;
 * see `contexts/auth/trackAuthAnalytics.ts`.
 */
export function pushRegister() {
  pushDataLayer({ event: 'register' });
}

/**
 * `click_mail` — the user clicked an email link (`mailto:`).
 * `mail` carries the raw address that was clicked so GTM can forward it.
 */
export function pushClickMail(mail: string) {
  pushDataLayer({ event: 'click_mail', mail });
}

/**
 * `copy_mail` — the user copied an email address (typically via a Link with
 * an `onCopy` handler on the listing detail page).
 */
export function pushCopyMail(mail: string) {
  pushDataLayer({ event: 'copy_mail', mail });
}

/**
 * `click_phone` — the user clicked a phone link (`tel:`).
 * `number` is normalised (spaces stripped) by the caller before being pushed.
 */
export function pushClickPhone(number: string) {
  pushDataLayer({ event: 'click_phone', number });
}

/**
 * `show_contact_info` — the user revealed a gated agent contact block on a
 * listing detail page (`ProtectedInformation`).
 */
export function pushShowContactInfo() {
  pushDataLayer({ event: 'show_contact_info' });
}

/**
 * `click_social` — the user clicked a share target in the share modal.
 * `social_channel` identifies the destination (e.g. `facebook`, `whatsapp`).
 */
export function pushClickSocial(social_channel: string) {
  pushDataLayer({ event: 'click_social', social_channel });
}

/**
 * `timer_three_min` — engagement signal fired when the user has remained on
 * the same route for three minutes. Driven by `useAnalyticsTimerThreeMin`,
 * which resets the timer on every pathname change.
 */
export function pushTimerThreeMin() {
  pushDataLayer({ event: 'timer_three_min' });
}
